import { useEffect, useState } from 'react'
import { Save, Settings } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { useAtualizarSchedule } from '../hooks/useJobs'
import {
  AtualizarJobScheduleDto,
  JobDefinicao,
  TipoExecucaoJob,
  TipoExecucaoJobValores,
  TipoExecucaoJobRotulo,
} from '../tipos/job'

interface ModalEditarJobProps {
  isOpen: boolean
  job: JobDefinicao | null
  onClose: () => void
}

const horarioParaInput = (horario?: string | null) => {
  if (!horario) return ''
  const partes = horario.split(':')
  if (partes.length < 2) return ''
  return `${partes[0].padStart(2, '0')}:${partes[1].padStart(2, '0')}`
}

const inputParaHorario = (valor: string) => {
  if (!valor) return null
  return `${valor}:00`
}

export function ModalEditarJob({ isOpen, job, onClose }: ModalEditarJobProps) {
  const [tipoExecucao, setTipoExecucao] = useState<TipoExecucaoJob>(
    TipoExecucaoJobValores.Intervalo,
  )
  const [horario, setHorario] = useState('')
  const [intervaloMinutos, setIntervaloMinutos] = useState<number | ''>('')
  const [diasRetroativos, setDiasRetroativos] = useState<number | ''>('')
  const [erro, setErro] = useState<string | null>(null)

  const atualizar = useAtualizarSchedule()

  useEffect(() => {
    if (!job) return
    setTipoExecucao(job.tipoExecucaoCodigo)
    setHorario(horarioParaInput(job.horarioDiario))
    setIntervaloMinutos(job.intervaloMinutos ?? '')
    setDiasRetroativos(job.diasRetroativos ?? 7)
    setErro(null)
  }, [job, isOpen])

  if (!job) return null

  const validar = (): AtualizarJobScheduleDto | null => {
    setErro(null)

    const dias = typeof diasRetroativos === 'number' ? diasRetroativos : Number(diasRetroativos)
    if (Number.isNaN(dias) || dias < 0 || dias > 90)
    {
      setErro('Dias retroativos deve estar entre 0 e 90.')
      return null
    }

    if (tipoExecucao === TipoExecucaoJobValores.Diaria) {
      if (!horario)
      {
        setErro('Informe o horário diário (HH:mm).')
        return null
      }
      return {
        tipoExecucaoCodigo: tipoExecucao,
        horarioDiario: inputParaHorario(horario),
        intervaloMinutos: null,
        diasRetroativos: dias,
      }
    }
    if (tipoExecucao === TipoExecucaoJobValores.Intervalo) {
      const minutos = typeof intervaloMinutos === 'number' ? intervaloMinutos : Number(intervaloMinutos)
      if (!minutos || minutos < 5)
      {
        setErro('Intervalo deve ser >= 5 minutos.')
        return null
      }
      return {
        tipoExecucaoCodigo: tipoExecucao,
        horarioDiario: null,
        intervaloMinutos: minutos,
        diasRetroativos: dias,
      }
    }
    return {
      tipoExecucaoCodigo: tipoExecucao,
      horarioDiario: null,
      intervaloMinutos: null,
      diasRetroativos: dias,
    }
  }

  const handleSalvar = async () => {
    const dto = validar()
    if (!dto) return
    await atualizar.mutateAsync({ id: job.id, data: dto })
    onClose()
  }

  const footer = (
    <div className="flex justify-end gap-3 px-6 py-4">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={handleSalvar}
        disabled={atualizar.isLoading}
        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4 mr-2 inline" />
        Atualizar
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Parametrizar: ${job.nome}`}
      subtitle="Defina como e com que frequência este job será executado"
      icon={(
        <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Settings className="w-5 h-5" />
        </div>
      )}
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de execução</label>
          <div className="space-y-2">
            {(Object.entries(TipoExecucaoJobValores) as [string, TipoExecucaoJob][]).map(([nome, valor]) => (
              <label key={nome} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="tipoExecucao"
                  checked={tipoExecucao === valor}
                  onChange={() => setTipoExecucao(valor)}
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">{TipoExecucaoJobRotulo[valor]}</div>
                  <div className="text-xs text-gray-600">
                    {valor === TipoExecucaoJobValores.Diaria && 'Executa uma vez por dia em horário fixo'}
                    {valor === TipoExecucaoJobValores.Intervalo && 'Executa de tempos em tempos (a cada X minutos)'}
                    {valor === TipoExecucaoJobValores.Manual && 'Não roda no agendamento; só executa pelo botão Executar agora'}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {tipoExecucao === TipoExecucaoJobValores.Diaria && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Horário diário</label>
            <input
              type="time"
              value={horario}
              onChange={(e) => setHorario(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            />
            <p className="mt-1 text-xs text-gray-500">Horário em UTC. O scheduler avalia a cada minuto.</p>
          </div>
        )}

        {tipoExecucao === TipoExecucaoJobValores.Intervalo && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Intervalo (minutos)</label>
            <input
              type="number"
              min={5}
              value={intervaloMinutos}
              onChange={(e) => setIntervaloMinutos(e.target.value === '' ? '' : Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
              placeholder="Ex: 30"
            />
            <p className="mt-1 text-xs text-gray-500">Mínimo 5 minutos para evitar rajadas em integrações externas.</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Dias retroativos</label>
          <input
            type="number"
            min={0}
            max={90}
            value={diasRetroativos}
            onChange={(e) => setDiasRetroativos(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
            placeholder="Ex: 7"
          />
          <p className="mt-1 text-xs text-gray-500">
            Janela de segurança em dias antes do cursor para capturar apontamentos retroativos / edições de entradas
            antigas (a API do Clockify só filtra por data do trabalho, não por data de modificação). Reaproveitar
            entradas já conhecidas não gera duplicidade. 0 = desligado. Sugestão: 7.
          </p>
        </div>

        {erro && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{erro}</div>
        )}
      </div>
    </Modal>
  )
}
